import React, { useState, useEffect } from 'react';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { InputTextarea } from 'primereact/inputtextarea';
import { Message } from 'primereact/message';
import { useCart } from '../../hooks/useCart';
import { useAuth } from '../../contexts/AuthContext';

// Formulario de Checkout unificado (envío + facturación + método de pago)
// Cumple con requisitos de datos mínimos, consentimiento legal y lógica de facturación (RUC/CE)
const CheckoutForm = ({ orderData, onOrderDataChange, onOrderComplete, user }) => {
  const { items, total: cartTotal } = useCart();
  const { user: authUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Datos de envío
  const [shippingData, setShippingData] = useState({
    email: user?.email || authUser?.email || '',
    documentNumber: '',
    firstName: user?.firstName || '',
    lastNamePaterno: '',
    lastNameMaterno: '',
    country: 'Perú',
    department: '',
    province: '',
    district: '',
    address: '',
    reference: '',
    postalCode: '',
    cellNumber: '',
    note: ''
  });

  // Facturación
  const [billingSameAsShipping, setBillingSameAsShipping] = useState(true);
  const [billingDocumentType, setBillingDocumentType] = useState(1); // 1: DNI, 6: RUC, 7: CE
  const [billingData, setBillingData] = useState({
    documentNumber: '',
    firstName: '',
    lastNamePaterno: '',
    lastNameMaterno: '',
    companyName: '',
    country: 'Perú',
    department: '',
    province: '',
    district: '',
    address: ''
  });

  const [legalConsent, setLegalConsent] = useState(false);

  // Pago
  const [paymentMethod, setPaymentMethod] = useState('bank'); // 'bank' | 'card'
  const [activePaymentIndex, setActivePaymentIndex] = useState(0); // 0: bank, 1: card
  const izipayFeePercent = parseFloat(process.env.REACT_APP_IZIPAY_FEE_PERCENT || '5');

  // Ubicaciones simples (demo). En producción, reemplazar con catálogo oficial.
  const locations = {
    Lima: {
      provinces: {
        Lima: ['Miraflores', 'San Isidro', 'Surco', 'Barranco'],
        Huaral: ['Huaral']
      }
    },
    Arequipa: {
      provinces: {
        Arequipa: ['Cercado', 'Yanahuara', 'Cayma']
      }
    }
  };

  const departmentOptions = Object.keys(locations).map((d) => ({ label: d, value: d }));
  const provinceOptions = (dept) => (dept && locations[dept]) ? Object.keys(locations[dept].provinces).map((p) => ({ label: p, value: p })) : [];
  const districtOptions = (dept, prov) => (dept && prov && locations[dept]?.provinces[prov]) ? locations[dept].provinces[prov].map((d) => ({ label: d, value: d })) : [];

  useEffect(() => {
    if (billingSameAsShipping) {
      setBillingData({
        documentNumber: shippingData.documentNumber,
        firstName: shippingData.firstName,
        lastNamePaterno: shippingData.lastNamePaterno,
        lastNameMaterno: shippingData.lastNameMaterno,
        companyName: '',
        country: shippingData.country,
        department: shippingData.department,
        province: shippingData.province,
        district: shippingData.district,
        address: shippingData.address
      });
    }
  }, [billingSameAsShipping, shippingData]);

  // Validación
  const validateForm = () => {
    const newErrors = {};
    if (!shippingData.email) newErrors.email = 'Correo electrónico es requerido';
    if (!shippingData.documentNumber) newErrors.documentNumber = 'Número de documento es requerido';
    if (!shippingData.firstName) newErrors.firstName = 'Nombres es requerido';
    if (!shippingData.lastNamePaterno) newErrors.lastNamePaterno = 'Apellido paterno es requerido';
    if (!shippingData.lastNameMaterno) newErrors.lastNameMaterno = 'Apellido materno es requerido';
    if (!shippingData.country) newErrors.country = 'País es requerido';
    if (!shippingData.department) newErrors.department = 'Departamento es requerido';
    if (!shippingData.province) newErrors.province = 'Provincia es requerida';
    if (!shippingData.district) newErrors.district = 'Distrito es requerido';
    if (!shippingData.address) newErrors.address = 'Dirección es requerida';
    if (!shippingData.cellNumber) newErrors.cellNumber = 'Número de celular es requerido';
    if (!legalConsent) newErrors.legalConsent = 'Debe aceptar la Política de Privacidad y tratamiento de datos';

    if (!billingSameAsShipping) {
      if (!billingData.documentNumber) newErrors.billingDocumentNumber = 'Documento (facturación) es requerido';
      if (![6, 7].includes(billingDocumentType)) {
        if (!billingData.firstName) newErrors.billingFirstName = 'Nombres (facturación) es requerido';
        if (!billingData.lastNamePaterno) newErrors.billingLastNamePaterno = 'Apellido paterno (facturación) es requerido';
        if (!billingData.lastNameMaterno) newErrors.billingLastNameMaterno = 'Apellido materno (facturación) es requerido';
      } else {
        if (!billingData.companyName) newErrors.companyName = 'Razón Social / Empresa es requerido';
      }
      if (!billingData.country) newErrors.billingCountry = 'País (facturación) es requerido';
      if (!billingData.department) newErrors.billingDepartment = 'Departamento (facturación) es requerido';
      if (!billingData.province) newErrors.billingProvince = 'Provincia (facturación) es requerida';
      if (!billingData.district) newErrors.billingDistrict = 'Distrito (facturación) es requerido';
      if (!billingData.address) newErrors.billingAddress = 'Dirección (facturación) es requerido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const extraFee = paymentMethod === 'card' ? (izipayFeePercent / 100) * cartTotal : 0;
  const shippingCost = cartTotal >= 2000 ? 0 : 35;

  useEffect(() => {
    onOrderDataChange?.({
      ...orderData,
      shipping: shippingData,
      billing: billingSameAsShipping
        ? { sameAsShipping: true }
        : { sameAsShipping: false, documentTypeId: billingDocumentType, ...billingData },
      payment: { method: paymentMethod, izipayFeePercent },
      notes: shippingData.note,
      extraFee,
      extraFeeLabel: paymentMethod === 'card' ? 'Recargo IziPay' : 'Recargo por método de pago',
      shippingCost
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingData, billingSameAsShipping, billingDocumentType, billingData, paymentMethod, extraFee, shippingCost]);

  const handleSubmitOrder = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1200));
      const orderResult = {
        id: 'ORD-' + Date.now(),
        shipping: shippingData,
        billing: billingSameAsShipping
          ? { sameAsShipping: true }
          : { sameAsShipping: false, documentTypeId: billingDocumentType, ...billingData },
        payment: { method: paymentMethod, izipayFeePercent },
        items,
        total: cartTotal + extraFee + shippingCost,
        notes: shippingData.note,
        status: 'pending',
        shippingCost
      };
      onOrderComplete?.(orderResult);
    } catch (err) {
      console.error('Error al procesar orden:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Envío */}
      {/*<Card title="Información de Envío" className="mb-4">
        <div className="grid formgrid">
          <div className="col-12 md:col-6 field">
            <label htmlFor="email" className="block text-sm font-medium mb-2">Correo electrónico *</label>
            <InputText id="email" value={shippingData.email} onChange={(e) => setShippingData({ ...shippingData, email: e.target.value })} className={`w-full ${errors.email ? 'p-invalid' : ''}`} />
            {errors.email && <small className="p-error">{errors.email}</small>}
          </div>
          <div className="col-12 md:col-6 field">
            <label htmlFor="documentNumber" className="block text-sm font-medium mb-2">Número de Documento *</label>
            <InputText id="documentNumber" value={shippingData.documentNumber} onChange={(e) => setShippingData({ ...shippingData, documentNumber: e.target.value })} className={`w-full ${errors.documentNumber ? 'p-invalid' : ''}`} />
            {errors.documentNumber && <small className="p-error">{errors.documentNumber}</small>}
          </div>
          <div className="col-12 md:col-4 field">
            <label htmlFor="firstName" className="block text-sm font-medium mb-2">Nombres *</label>
            <InputText id="firstName" value={shippingData.firstName} onChange={(e) => setShippingData({ ...shippingData, firstName: e.target.value })} className={`w-full ${errors.firstName ? 'p-invalid' : ''}`} />
            {errors.firstName && <small className="p-error">{errors.firstName}</small>}
          </div>
          <div className="col-12 md:col-4 field">
            <label htmlFor="lastNamePaterno" className="block text-sm font-medium mb-2">Apellido paterno *</label>
            <InputText id="lastNamePaterno" value={shippingData.lastNamePaterno} onChange={(e) => setShippingData({ ...shippingData, lastNamePaterno: e.target.value })} className={`w-full ${errors.lastNamePaterno ? 'p-invalid' : ''}`} />
            {errors.lastNamePaterno && <small className="p-error">{errors.lastNamePaterno}</small>}
          </div>
          <div className="col-12 md:col-4 field">
            <label htmlFor="lastNameMaterno" className="block text-sm font-medium mb-2">Apellido materno *</label>
            <InputText id="lastNameMaterno" value={shippingData.lastNameMaterno} onChange={(e) => setShippingData({ ...shippingData, lastNameMaterno: e.target.value })} className={`w-full ${errors.lastNameMaterno ? 'p-invalid' : ''}`} />
            {errors.lastNameMaterno && <small className="p-error">{errors.lastNameMaterno}</small>}
          </div>

          <div className="col-12 md:col-6 field">
            <label htmlFor="country" className="block text-sm font-medium mb-2">País *</label>
            <InputText id="country" value={shippingData.country} disabled className={`w-full ${errors.country ? 'p-invalid' : ''}`} />
            {errors.country && <small className="p-error">{errors.country}</small>}
          </div>
          <div className="col-12 md:col-6 field">
            <label htmlFor="department" className="block text-sm font-medium mb-2">Departamento *</label>
            <Dropdown id="department" value={shippingData.department} options={departmentOptions} onChange={(e) => setShippingData({ ...shippingData, department: e.value, province: '', district: '' })} placeholder="Seleccione" className={`w-full ${errors.department ? 'p-invalid' : ''}`} />
            {errors.department && <small className="p-error">{errors.department}</small>}
          </div>
          <div className="col-12 md:col-6 field">
            <label htmlFor="province" className="block text-sm font-medium mb-2">Provincia *</label>
            <Dropdown id="province" value={shippingData.province} options={provinceOptions(shippingData.department)} onChange={(e) => setShippingData({ ...shippingData, province: e.value, district: '' })} placeholder="Seleccione" className={`w-full ${errors.province ? 'p-invalid' : ''}`} />
            {errors.province && <small className="p-error">{errors.province}</small>}
          </div>
          <div className="col-12 md:col-6 field">
            <label htmlFor="district" className="block text-sm font-medium mb-2">Distrito *</label>
            <Dropdown id="district" value={shippingData.district} options={districtOptions(shippingData.department, shippingData.province)} onChange={(e) => setShippingData({ ...shippingData, district: e.value })} placeholder="Seleccione" className={`w-full ${errors.district ? 'p-invalid' : ''}`} />
            {errors.district && <small className="p-error">{errors.district}</small>}
          </div>

          <div className="col-12 field">
            <label htmlFor="address" className="block text-sm font-medium mb-2">Dirección *</label>
            <InputText id="address" value={shippingData.address} onChange={(e) => setShippingData({ ...shippingData, address: e.target.value })} className={`w-full ${errors.address ? 'p-invalid' : ''}`} />
            {errors.address && <small className="p-error">{errors.address}</small>}
          </div>
          <div className="col-12 field">
            <label htmlFor="reference" className="block text-sm font-medium mb-2">Referencia</label>
            <InputText id="reference" value={shippingData.reference} onChange={(e) => setShippingData({ ...shippingData, reference: e.target.value })} className="w-full" />
          </div>
          <div className="col-12 md:col-6 field">
            <label htmlFor="postalCode" className="block text-sm font-medium mb-2">Código Postal</label>
            <InputText id="postalCode" value={shippingData.postalCode} onChange={(e) => setShippingData({ ...shippingData, postalCode: e.target.value })} className="w-full" />
          </div>
          <div className="col-12 md:col-6 field">
            <label htmlFor="cellNumber" className="block text-sm font-medium mb-2">Número de celular *</label>
            <InputText id="cellNumber" value={shippingData.cellNumber} onChange={(e) => setShippingData({ ...shippingData, cellNumber: e.target.value })} className={`w-full ${errors.cellNumber ? 'p-invalid' : ''}`} />
            {errors.cellNumber && <small className="p-error">{errors.cellNumber}</small>}
          </div>
          <div className="col-12 field">
            <label htmlFor="note" className="block text-sm font-medium mb-2">Nota para entrega o envío</label>
            <InputTextarea id="note" value={shippingData.note} onChange={(e) => setShippingData({ ...shippingData, note: e.target.value })} className="w-full" rows={3} />
          </div>

          <div className="col-12 field">
            <div className="flex align-items-start">
              <Checkbox inputId="legalConsent" checked={legalConsent} onChange={(e) => setLegalConsent(e.checked)} binary />
              <label htmlFor="legalConsent" className="ml-2">Acepto la <a href="/politica-de-privacidad" target="_blank" rel="noopener noreferrer">Política de Privacidad</a> y el tratamiento de mis datos personales conforme a la Ley N° 29733.</label>
            </div>
            {errors.legalConsent && <small className="p-error">{errors.legalConsent}</small>}
          </div>
        </div>
      </Card>*/}

      {/* Facturación */}
      <Card title="Datos de Facturación" className="mb-4">
        <div className="field mb-3">
          {/*<div className="flex align-items-center">
            <Checkbox inputId="billingSame" checked={billingSameAsShipping} onChange={(e) => setBillingSameAsShipping(e.checked)} binary />
            <label htmlFor="billingSame" className="ml-2">Usar datos de envío para facturación</label>
          </div>*/}
          <div className="flex align-items-center">
            <Message severity="warn" text={`Importante: Actualmente estamos reestructurando nuestros medios de pago automatizado, para generar su pedido por favor escríbanos por medio de nuestro número Whatsapp +51 978 326 796 para realizar su pedido, disculpe las molestias.`} className="mb-3" />
          </div>
        </div>

        {!billingSameAsShipping && (
          <div className="grid formgrid">
            <div className="col-12 md:col-6 field">
              <label htmlFor="documentType" className="block text-sm font-medium mb-2">Tipo de Documento</label>
              <Dropdown
                id="documentType"
                value={billingDocumentType}
                options={[
                  { label: 'DNI', value: 1 },
                  { label: 'RUC', value: 6 },
                  { label: 'Carné de Extranjería', value: 7 }
                ]}
                onChange={(e) => setBillingDocumentType(e.value)}
                className="w-full"
              />
            </div>
            <div className="col-12 md:col-6 field">
              <label htmlFor="billingDocumentNumber" className="block text-sm font-medium mb-2">Número de Documento *</label>
              <InputText id="billingDocumentNumber" value={billingData.documentNumber} onChange={(e) => setBillingData({ ...billingData, documentNumber: e.target.value })} className={`w-full ${errors.billingDocumentNumber ? 'p-invalid' : ''}`} />
              {errors.billingDocumentNumber && <small className="p-error">{errors.billingDocumentNumber}</small>}
            </div>

            {[6, 7].includes(billingDocumentType) ? (
              <div className="col-12 field">
                <label htmlFor="companyName" className="block text-sm font-medium mb-2">Razón Social / Empresa *</label>
                <InputText id="companyName" value={billingData.companyName} onChange={(e) => setBillingData({ ...billingData, companyName: e.target.value })} className={`w-full ${errors.companyName ? 'p-invalid' : ''}`} />
                {errors.companyName && <small className="p-error">{errors.companyName}</small>}
              </div>
            ) : (
              <>
                <div className="col-12 md:col-4 field">
                  <label htmlFor="billingFirstName" className="block text-sm font-medium mb-2">Nombres *</label>
                  <InputText id="billingFirstName" value={billingData.firstName} onChange={(e) => setBillingData({ ...billingData, firstName: e.target.value })} className={`w-full ${errors.billingFirstName ? 'p-invalid' : ''}`} />
                  {errors.billingFirstName && <small className="p-error">{errors.billingFirstName}</small>}
                </div>
                <div className="col-12 md:col-4 field">
                  <label htmlFor="billingLastNamePaterno" className="block text-sm font-medium mb-2">Apellido paterno *</label>
                  <InputText id="billingLastNamePaterno" value={billingData.lastNamePaterno} onChange={(e) => setBillingData({ ...billingData, lastNamePaterno: e.target.value })} className={`w-full ${errors.billingLastNamePaterno ? 'p-invalid' : ''}`} />
                  {errors.billingLastNamePaterno && <small className="p-error">{errors.billingLastNamePaterno}</small>}
                </div>
                <div className="col-12 md:col-4 field">
                  <label htmlFor="billingLastNameMaterno" className="block text-sm font-medium mb-2">Apellido materno *</label>
                  <InputText id="billingLastNameMaterno" value={billingData.lastNameMaterno} onChange={(e) => setBillingData({ ...billingData, lastNameMaterno: e.target.value })} className={`w-full ${errors.billingLastNameMaterno ? 'p-invalid' : ''}`} />
                  {errors.billingLastNameMaterno && <small className="p-error">{errors.billingLastNameMaterno}</small>}
                </div>
              </>
            )}

            <div className="col-12 md:col-6 field">
              <label htmlFor="billingCountry" className="block text-sm font-medium mb-2">País *</label>
              <InputText id="billingCountry" value={billingData.country} disabled className={`w-full ${errors.billingCountry ? 'p-invalid' : ''}`} />
              {errors.billingCountry && <small className="p-error">{errors.billingCountry}</small>}
            </div>
            <div className="col-12 md:col-6 field">
              <label htmlFor="billingDepartment" className="block text-sm font-medium mb-2">Departamento *</label>
              <Dropdown id="billingDepartment" value={billingData.department} options={departmentOptions} onChange={(e) => setBillingData({ ...billingData, department: e.value, province: '', district: '' })} placeholder="Seleccione" className={`w-full ${errors.billingDepartment ? 'p-invalid' : ''}`} />
              {errors.billingDepartment && <small className="p-error">{errors.billingDepartment}</small>}
            </div>
            <div className="col-12 md:col-6 field">
              <label htmlFor="billingProvince" className="block text-sm font-medium mb-2">Provincia *</label>
              <Dropdown id="billingProvince" value={billingData.province} options={provinceOptions(billingData.department)} onChange={(e) => setBillingData({ ...billingData, province: e.value, district: '' })} placeholder="Seleccione" className={`w-full ${errors.billingProvince ? 'p-invalid' : ''}`} />
              {errors.billingProvince && <small className="p-error">{errors.billingProvince}</small>}
            </div>
            <div className="col-12 md:col-6 field">
              <label htmlFor="billingDistrict" className="block text-sm font-medium mb-2">Distrito *</label>
              <Dropdown id="billingDistrict" value={billingData.district} options={districtOptions(billingData.department, billingData.province)} onChange={(e) => setBillingData({ ...billingData, district: e.value })} placeholder="Seleccione" className={`w-full ${errors.billingDistrict ? 'p-invalid' : ''}`} />
              {errors.billingDistrict && <small className="p-error">{errors.billingDistrict}</small>}
            </div>
            <div className="col-12 field">
              <label htmlFor="billingAddress" className="block text-sm font-medium mb-2">Dirección *</label>
              <InputText id="billingAddress" value={billingData.address} onChange={(e) => setBillingData({ ...billingData, address: e.target.value })} className={`w-full ${errors.billingAddress ? 'p-invalid' : ''}`} />
              {errors.billingAddress && <small className="p-error">{errors.billingAddress}</small>}
            </div>
          </div>
        )}
      </Card>

      {/* Pago */}
      <Card title="Método de Pago" className="mb-4">
        <Accordion
          activeIndex={activePaymentIndex}
          onTabChange={(e) => {
            const idx = e.index ?? 0;
            setActivePaymentIndex(idx);
            setPaymentMethod(idx === 1 ? 'card' : 'bank');
          }}
        >
          <AccordionTab header="Transferencia / Depósito (Interbank)">
            <div className="p-3">
              <p className="text-sm mb-3">Realiza una transferencia o depósito con los siguientes datos:</p>
              <div className="p-3 border-1 surface-border border-round mb-3">
                <div className="mb-2"><strong>RUC:</strong> 20612859630</div>
                <div className="mb-2"><strong>Titular:</strong> DIGITAL WINS E.I.R.L.</div>
                <div className="mb-2"><strong>Cuenta corriente Soles Interbank:</strong> 2003007391739</div>
                <div className="mb-2"><strong>Cuenta corriente Interbancario (CCI) Soles Interbank:</strong> 00320000300739173932</div>
              </div>
              <p className="text-sm">Envía el comprobante a <a href="mailto:ventas@laptops.pe">ventas@laptops.pe</a> o vía WhatsApp. Tu pedido se procesará al confirmar el pago.</p>
            </div>
          </AccordionTab>
          <AccordionTab header="Tarjetas (IziPay)">
            <div className="p-3">
              <Message severity="info" text={`Este método añade ${izipayFeePercent}% al total de la compra.`} className="mb-3" />
              <p className="text-sm mb-2">Pagos con tarjeta serán procesados por IziPay.</p>
              <p className="text-sm text-color-secondary">En esta etapa mostraremos el formulario de IziPay cuando se integre oficialmente.</p>
              {/* Placeholder para formulario de IziPay a integrar */}
              <div className="p-3 border-2 border-dashed surface-border border-round text-center">
                <i className="pi pi-credit-card text-2xl text-color-secondary mb-2"></i>
                <p className="m-0 text-sm">Formulario de pago IziPay (próximamente)</p>
              </div>
            </div>
          </AccordionTab>
        </Accordion>
      </Card>

      <div className="flex justify-content-end">
        {/*<Button label={loading ? 'Procesando...' : 'Confirmar Pedido'} className="p-button-success" onClick={handleSubmitOrder} disabled={loading} icon="pi pi-check" />*/}
      </div>
    </div>
  );
};

export default CheckoutForm;